/**
 * Confirm archiving one row: what it hides, who still uses it (datasets and
 * evaluators count experiments, scores count queues) and that it can come
 * back. After it, a toast with Undo for 8s. A 409 the caller knows (a running
 * experiment) goes to `onConflict` instead of a toast.
 *
 * `mutateAsync`, not per-call callbacks: archiving refetches the list, the
 * row and so this dialog unmount before it resolves, and TanStack drops a
 * per-call `onSuccess` once its observer is gone.
 */

import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/overlay";
import { errorText } from "@/components/ui/states";
import { ApiError } from "@/lib/api";
import { plural } from "@/lib/format";
import { useApiQuery } from "@/lib/query";
import { useUiStore } from "@/stores/ui-store";
import { refocus } from "./focus";
import { useArchive, useRestore } from "./hooks";
import { KINDS, type ArchiveKind, type UsedBy } from "./kinds";

export interface ArchiveTarget {
  id: string;
  name: string;
}

export function ArchiveDialog({
  kind,
  target,
  onClose,
  onArchived,
  onConflict,
}: {
  kind: ArchiveKind;
  target: ArchiveTarget;
  onClose: () => void;
  /** After a successful archive, e.g. leave the detail page for the list. */
  onArchived?: (() => void) | undefined;
  /** Return true when the 409 was handled (shown some other way). */
  onConflict?: ((error: ApiError) => boolean) | undefined;
}) {
  const info = KINDS[kind];
  const archive = useArchive(kind);
  const restore = useRestore(kind);
  const notify = useUiStore((s) => s.notify);
  // Shares the detail page's cache: usually there already.
  const detail = useApiQuery<UsedBy>(`${info.path}/${target.id}`, {}, { enabled: Boolean(info.usedBy) });
  const used = info.usedBy ? (detail.data?.used_by?.[info.usedBy] ?? 0) : 0;
  // Hold Archive until we know who still uses it, so a fast click can't skip the warning.
  const counting = Boolean(info.usedBy) && detail.isPending;

  const confirm = () =>
    archive.mutateAsync(target.id).then(
      () => {
        onClose();
        onArchived?.();
        notify(`Archived "${target.name}".`, "success", {
          label: "Undo",
          run: () => void restore(target, true).then(refocus),
        });
        refocus();
      },
      (err: unknown) => {
        if (err instanceof ApiError && err.status === 409 && onConflict?.(err)) return;
        notify(errorText(err, `Could not archive "${target.name}". Try again.`), "error");
      },
    );

  return (
    <Dialog
      title={`Archive "${target.name}"?`}
      onClose={onClose}
      onSubmit={() => !archive.isPending && !counting && confirm()}
      width={480}
      footer={
        <>
          <span className="flex-1" />
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={archive.isPending || counting}>
            Archive {info.noun}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-2.5 text-[13px] leading-relaxed text-body">
        <p>
          Hidden from the {info.list} list{info.hiddenFrom}.
        </p>
        {info.keeps && <p>{info.keeps}</p>}
        {counting && (
          <p className="rounded-[9px] bg-subtle px-3.5 py-2.5 text-muted" role="status">
            Checking where it's used…
          </p>
        )}
        {info.usedBy && used > 0 && (
          <p className="rounded-[9px] bg-subtle px-3.5 py-2.5">
            Used in {plural(used, info.usedBy.slice(0, -1))}. They keep it; it won't be offered for new ones.
          </p>
        )}
        <p className="text-muted">You can restore it any time from Archived.</p>
      </div>
    </Dialog>
  );
}
